/* eslint-disable func-names */
import { site } from '@panel/commons/helpers';
import Axios from 'axios';
import * as yup from 'yup';

const ONLY_NUMBERS = /^\d+$/;
const CNPJ_REGEX = /^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/;
const CPF_REGEX = /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/;
const CUIT_CUIL_REGEX = /^\d{2}(\d){7,8}\d{1}$/;
const CUIT_CUIL_DNI_REGEX = /^\d{2}(\d){4,8}\d{1}$/;
const CURP_REGEX = /^[a-zA-Z0-9]{18}$/;
const RFC_PERSONA_MORAL_REGEX = /^(([A-Z,Ñ,&]|[a-z,ñ,&]){3})([0-9]{6})((([A-Z]|[a-z]|[0-9]){3}))$/;
const RFC_PERSONA_FISICA_REGEX = /^(([A-Z,Ñ,&]|[a-z,ñ,&]|\\s){1})(([A-Z]|[a-z]){3})([0-9]{6})((([A-Z]|[a-z]|[0-9]){3}))$/;
const DNI_PERU_REGEX = /^\d{8,9}$/;
const RUC_PERU_REGEX = /^\d{11}$/;
const LEGAL_NAME_REGEX = /^(?=(\W*\w){3}).*$/;
const REPEATED_DIGITS = /^(\d)\1*$/;
const CONSECUTIVE_PATTERN = /(01234|12345|23456|34567|45678|56789|67890|98765|87654|76543|65432|54321|43210)/;
const siteId = process.env.REACT_APP_BUILD_SITE;
const API_ECOM = process.env.REACT_APP_API_ECOMMERCE;

const commonValidations = yup.object().shape({
  taxCondition: yup.string().required(),
  taxId: yup.string().required('publisher.form.convertseeker.idnumber.validation'),
  phone: yup.string().max(40, 'publisher.form.convertseeker.phone.toolong'),
  mobilePhone: yup
    .string()
    .max(40, 'publisher.form.convertseeker.mobilePhone.tooLong')
    .required('publisher.form.convertseeker.mobilePhone.validation')
});

const commonLegalNameValidation = fieldTranslation =>
  yup
    .string()
    .trim()
    .max(35, `publisher.form.convertseeker.${fieldTranslation}.toolong`)
    .required(`publisher.form.convertseeker.${fieldTranslation}.validation`);

const brasilTaxConditionValidation = yup.string().when('taxCondition', {
  is: val => val === 9 || val === '9',
  then: yup
    .string()
    .matches(CNPJ_REGEX, 'publisher.form.convertseeker.idnumber.validation')
    .required('publisher.form.convertseeker.idnumber.validation'),
  otherwise: yup
    .string()
    .matches(CPF_REGEX, 'publisher.form.convertseeker.idnumber.validation')
    .required('publisher.form.convertseeker.idnumber.validation')
});

const peruTaxConditionValidation = yup
  .string()
  .trim()
  .max(11, 'publisher.form.convertseeker.idnumber.validation')
  .when('taxCondition', (taxCondition, schema) => {
    if (taxCondition === '302' || taxCondition === '324') {
      return schema
        .matches(ONLY_NUMBERS, 'publisher.form.convertseeker.idnumber.validation.onlynumbers')
        .matches(RUC_PERU_REGEX, 'publisher.form.convertseeker.idnumber.validation');
    }
    if (taxCondition === '314' || taxCondition === '325') {
      return schema
        .matches(ONLY_NUMBERS, 'publisher.form.convertseeker.idnumber.validation.onlynumbers')
        .matches(DNI_PERU_REGEX, 'publisher.form.convertseeker.idnumber.validation');
    }
    return schema.matches(ONLY_NUMBERS, 'publisher.form.convertseeker.idnumber.validation.onlynumbers');
  })
  .required('publisher.form.convertseeker.idnumber.validation');

const customValidations = {
  ZPAR: yup.object().shape({
    firstName: yup
      .string()
      .trim()
      .max(35, 'publisher.form.convertseeker.name.toolong')
      .required('publisher.form.convertseeker.name.validation')
      .matches(LEGAL_NAME_REGEX, 'publisher.form.convertseeker.name.alphanumeric'),
    lastName: yup
      .string()
      .trim()
      .max(35, 'publisher.form.convertseeker.lastname.toolong')
      .required('publisher.form.convertseeker.lastname.validation')
      .matches(LEGAL_NAME_REGEX, 'publisher.form.convertseeker.lastname.alphanumeric'),
    taxId: yup
      .string()
      .trim()
      .when('taxCondition', (taxCondition, schema) => {
        if (taxCondition === '4') {
          return schema
            .min(7, 'publisher.form.convertseeker.idnumber.validation')
            .max(11, 'publisher.form.convertseeker.idnumber.validation')
            .matches(CUIT_CUIL_DNI_REGEX, 'publisher.form.convertseeker.idnumber.numeric');
        }
        if (taxCondition === '13') {
          return schema
            .min(3, 'publisher.form.convertseeker.idnumber.validation')
            .max(20, 'publisher.form.convertseeker.idnumber.validation');
        }
        return schema
          .min(10, 'publisher.form.convertseeker.idnumber.validation')
          .max(11, 'publisher.form.convertseeker.idnumber.validation')
          .matches(CUIT_CUIL_REGEX, 'publisher.form.convertseeker.idnumber.validation');
      })
      .required('publisher.form.convertseeker.idnumber.validation')
  }),
  IWBR: yup.object().shape({
    firstName: commonLegalNameValidation('name'),
    lastName: commonLegalNameValidation('lastname'),
    taxId: brasilTaxConditionValidation
  }),
  WIBR: yup.object().shape({
    firstName: commonLegalNameValidation('name'),
    lastName: commonLegalNameValidation('lastname'),
    taxId: brasilTaxConditionValidation
  }),
  ICBR: yup.object().shape({
    firstName: commonLegalNameValidation('name'),
    lastName: commonLegalNameValidation('lastname'),
    taxId: brasilTaxConditionValidation
  }),
  DVPE: yup.object().shape({
    firstName: yup
      .string()
      .trim()
      .max(40, 'publisher.form.convertseeker.name.toolong')
      .required('publisher.form.convertseeker.name.validation')
      .matches(LEGAL_NAME_REGEX, 'publisher.form.convertseeker.name.alphanumeric'),
    lastName: yup
      .string()
      .trim()
      .max(40, 'publisher.form.convertseeker.lastname.toolong')
      .required('publisher.form.convertseeker.lastname.validation')
      .matches(LEGAL_NAME_REGEX, 'publisher.form.convertseeker.lastname.alphanumeric'),
    taxId: peruTaxConditionValidation
  }),
  UBPE: yup.object().shape({
    firstName: yup
      .string()
      .trim()
      .max(40, 'publisher.form.convertseeker.name.toolong')
      .required('publisher.form.convertseeker.name.validation')
      .matches(LEGAL_NAME_REGEX, 'publisher.form.convertseeker.name.alphanumeric'),
    lastName: yup
      .string()
      .trim()
      .max(40, 'publisher.form.convertseeker.lastname.toolong')
      .required('publisher.form.convertseeker.lastname.validation')
      .matches(LEGAL_NAME_REGEX, 'publisher.form.convertseeker.lastname.alphanumeric'),
    taxId: peruTaxConditionValidation
  }),
  '24MX': yup.object().shape({
    firstName: commonLegalNameValidation('name'),
    lastName: commonLegalNameValidation('lastname'),
    taxId: yup
      .string()
      .trim()
      .max(18, 'publisher.form.convertseeker.idnumber.validation')
      .when('taxCondition', (taxCondition, schema) => {
        if (taxCondition === '6') {
          return schema.matches(CURP_REGEX, 'publisher.form.convertseeker.idnumber.validation');
        }
        if (taxCondition === '301') {
          return schema.matches(RFC_PERSONA_MORAL_REGEX, 'publisher.form.convertseeker.idnumber.validation');
        }
        return schema.matches(RFC_PERSONA_FISICA_REGEX, 'publisher.form.convertseeker.idnumber.validation');
      })
      .required('publisher.form.convertseeker.idnumber.validation'),
    mobilePhone: yup
      .string()
      .max(20, 'panel.profileSelection.full.phone.error')
      .required('publisher.form.convertseeker.mobilePhone.validation')
      .test('no-consecutive-or-same-digits', 'panel.profileSelection.full.phone.error', value => {
        if (!value) {
          return true;
        }
        return !CONSECUTIVE_PATTERN.test(value) && !REPEATED_DIGITS.test(value);
      })
      .test('check-phone', 'panel.profileSelection.full.phone.error', async function checkPhone(value) {
        if (value) {
          const url = `${API_ECOM}/ecommerce/api/v1/validation/phone/${value}`;
          const headers = {
            'x-panel-portal': siteId
          };
          const result = await Axios.get(url, { headers: { ...headers } })
            .then(resp => resp.data.valid)
            .catch(() => true);
          return result;
        }
        return true;
      })
  }),
  VAMX: yup.object().shape({
    firstName: commonLegalNameValidation('name'),
    lastName: commonLegalNameValidation('lastname'),
    taxId: yup
      .string()
      .trim()
      .max(18, 'publisher.form.convertseeker.idnumber.validation')
      .when('taxCondition', (taxCondition, schema) => {
        if (taxCondition === '1006') {
          return schema.matches(CURP_REGEX, 'publisher.form.convertseeker.idnumber.validation');
        }
        if (taxCondition === '1301') {
          return schema.matches(RFC_PERSONA_MORAL_REGEX, 'publisher.form.convertseeker.idnumber.validation');
        }
        return schema.matches(RFC_PERSONA_FISICA_REGEX, 'publisher.form.convertseeker.idnumber.validation');
      })
      .required('publisher.form.convertseeker.idnumber.validation'),
    mobilePhone: yup
      .string()
      .max(20, 'panel.profileSelection.full.phone.error')
      .required('publisher.form.convertseeker.mobilePhone.validation')
      .test('no-consecutive-or-same-digits', 'panel.profileSelection.full.phone.error', value => {
        if (!value) {
          return true;
        }
        return !CONSECUTIVE_PATTERN.test(value) && !REPEATED_DIGITS.test(value);
      })
      .test('check-phone', 'panel.profileSelection.full.phone.error', async function checkPhone(value) {
        if (value) {
          const url = `${API_ECOM}/ecommerce/api/v1/validation/phone/${value}`;
          const headers = {
            'x-panel-portal': siteId
          };
          const result = await Axios.get(url, { headers: { ...headers } })
            .then(resp => resp.data.valid)
            .catch(() => true);
          return result;
        }
        return true;
      })
  }),
  CAPA: yup.object().shape({
    firstName: yup
      .string()
      .trim()
      .max(35, 'publisher.form.convertseeker.name.toolong')
      .required('publisher.form.convertseeker.name.validation')
      .matches(LEGAL_NAME_REGEX, 'publisher.form.convertseeker.name.validation"'),
    lastName: yup
      .string()
      .trim()
      .max(35, 'publisher.form.convertseeker.lastname.toolong')
      .required('publisher.form.convertseeker.lastname.validation')
      .matches(LEGAL_NAME_REGEX, 'publisher.form.convertseeker.lastName.validation"')
  }),
  PLEC: yup.object().shape({
    firstName: yup
      .string()
      .trim()
      .max(35, 'publisher.form.convertseeker.name.toolong')
      .required('publisher.form.convertseeker.name.validation')
      .matches(LEGAL_NAME_REGEX, 'publisher.form.convertseeker.name.validation"'),
    lastName: yup
      .string()
      .trim()
      .max(35, 'publisher.form.convertseeker.lastname.toolong')
      .required('publisher.form.convertseeker.lastname.validation')
      .matches(LEGAL_NAME_REGEX, 'publisher.form.convertseeker.lastName.validation')
  })
};
const portalValidations = customValidations[site.id];
const seekerFormValidations = portalValidations ? commonValidations.concat(portalValidations) : commonValidations;

export { seekerFormValidations };
